import { IDropdownOption, Stack } from '@fluentui/react';
import { PageHeader, useHaicPageTitle } from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Grouping as H2OGPTeGrouping } from '../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Grouping } from '../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { stackStylesNoLeftNav } from '../../themes/themes';
import { useEnv } from '../../utils/hooks';
import { ChartContainer } from './components/ChartContainer';
import { DateRangeCallout } from './components/DateRangeCallout';
import { FilterPanel } from './components/FilterPanel';
import { MetricsCards } from './components/MetricsCards';
import { TableContainer } from './components/TableContainer';
import { PlatformUsageConfigProvider } from './config';
import { granularityOptions } from './constants/granularity';
import { groupByOptions } from './constants/grouping';
import { h2ogpteGroupByOptions } from './constants/h2ogpteGrouping';
import { ResourceType } from './constants/resource';
import { TokenUsageType } from './constants/tokenUsageType';
import {
  useDateRangeState,
  useEnrichedWorkspaces,
  useFilteredTokenUsageData,
  useFilteredUsageData,
  useFormattedUsageData,
  useGroupColors,
  useMultiDimensionalPrefetch,
  usePlatformUsageUrlSync,
  usePrefetchedFilterOptions,
  useServiceFilters,
  useTagFilters,
  useUserFilters,
  useWorkspaceFilters,
} from './hooks';
import { useFormattedTokenUsageData } from './hooks/useFormattedTokenUsageData';
import { useModelFilters } from './hooks/useModelFilters';
import { useMultiDimensionalTokenPrefetch } from './hooks/useMultiDimensionalTokenPrefetch';
import { usePrefetchedTokenFilterOptions } from './hooks/usePrefetchedTokenFilterOptions';
import { useTokenUserFilters } from './hooks/useTokenUserFilters';
import { useTokenWorkspaceFilters } from './hooks/useTokenWorkspaceFilters';
import { TokenUsageStats, UsageStats } from './types';
import { calculateUsageStats } from './utils/calculations';
import { calculateTokenUsageStats } from './utils/tokenCalculations';

const pageTitle = 'Platform Usage';
const subtitle = 'Monitor AI unit consumption and token usage across your platform';

function PlatformUsageContent() {
  const env = useEnv();
  useHaicPageTitle(pageTitle, env?.cloudInstanceName);

  // URL-synced state for grouping, resource type, and date range
  // URL is the single source of truth for all persistent state
  const urlSync = usePlatformUsageUrlSync();
  const { resourceType, setResourceType, groupBy, setGroupBy, h2ogpteGroupBy, setH2OGPTeGroupBy } = urlSync;

  // Token usage type (not URL-synced)
  const [tokenUsageType, setTokenUsageType] = useState<TokenUsageType>(TokenUsageType.TOTAL);

  // Date range state - values from URL, callout UI managed locally
  const dateRangeState = useDateRangeState({
    startTime: urlSync.startTime,
    endTime: urlSync.endTime,
    granularity: urlSync.granularity,
    dateRangeType: urlSync.dateRangeType,
    setDateRange: urlSync.setDateRange,
  });
  const enrichedWorkspaces = useEnrichedWorkspaces();

  // Fetch UNFILTERED usage data - prefetches all time range and grouping combinations
  // This data is used to extract filter options (workspaces, users, services)
  // Filter options should always show ALL entities in the date range, not just filtered ones
  const usageDataResult = useMultiDimensionalPrefetch(
    [], // Always empty - fetch unfiltered data for filter options
    [], // Always empty - fetch unfiltered data for filter options
    [], // Always empty - fetch unfiltered data for filter options
    [], // Always empty - fetch unfiltered data for filter options
    {
      startTime: dateRangeState.startTime,
      endTime: dateRangeState.endTime,
      granularity: dateRangeState.granularity,
    },
    groupBy
  );

  // Extract filter options from the prefetched workspace, user, tag, and service data
  const filterOptions = usePrefetchedFilterOptions(
    usageDataResult.workspaceData,
    usageDataResult.userData,
    usageDataResult.tagData,
    usageDataResult.serviceData,
    enrichedWorkspaces.workspacesMap,
    enrichedWorkspaces.usersMap
  );

  // Initialize workspace and user filters with the extracted options
  const workspaceFilters = useWorkspaceFilters(
    filterOptions.workspaceOptions,
    dateRangeState.startTime,
    dateRangeState.endTime,
    dateRangeState.granularity
  );

  const userFilters = useUserFilters(
    filterOptions.userOptions,
    dateRangeState.startTime,
    dateRangeState.endTime,
    dateRangeState.granularity
  );

  const tagFilters = useTagFilters(
    filterOptions.tagOptions,
    dateRangeState.startTime,
    dateRangeState.endTime,
    dateRangeState.granularity
  );

  const serviceFilters = useServiceFilters(
    filterOptions.serviceOptions,
    dateRangeState.startTime,
    dateRangeState.endTime,
    dateRangeState.granularity
  );

  // ============================================
  // H2OGPTe Token Usage Hooks
  // ============================================

  // Check if H2OGPTe is selected
  const isH2oGPTeSelected = resourceType === ResourceType.H2OGPTE_TOKENS;

  // Prefetch all token groupings (WORKSPACE, USER, MODEL) for instant switching and filter options
  const tokenPrefetchResult = useMultiDimensionalTokenPrefetch(
    [], // Empty - fetch unfiltered data for filter options
    [],
    [],
    {
      startTime: dateRangeState.startTime,
      endTime: dateRangeState.endTime,
      granularity: dateRangeState.granularity,
    },
    h2ogpteGroupBy
  );

  // Extract filter options from prefetched token data
  const tokenFilterOptions = usePrefetchedTokenFilterOptions(
    tokenPrefetchResult.modelData,
    tokenPrefetchResult.workspaceData,
    tokenPrefetchResult.userData,
    enrichedWorkspaces.workspacesMap,
    enrichedWorkspaces.usersMap
  );

  const tokenModelOptions = tokenFilterOptions.modelOptions;

  // Token-specific filter hooks
  const tokenWorkspaceFilters = useTokenWorkspaceFilters(
    tokenFilterOptions.workspaceOptions,
    dateRangeState.startTime,
    dateRangeState.endTime,
    dateRangeState.granularity
  );

  const tokenUserFilters = useTokenUserFilters(
    tokenFilterOptions.userOptions,
    dateRangeState.startTime,
    dateRangeState.endTime,
    dateRangeState.granularity
  );

  // Model filters hook
  const modelFilters = useModelFilters(
    tokenModelOptions,
    dateRangeState.startTime,
    dateRangeState.endTime,
    dateRangeState.granularity
  );

  // Determine if any token filters are currently applied
  const hasTokenFiltersApplied =
    modelFilters.appliedModels.length > 0 ||
    tokenWorkspaceFilters.appliedWorkspaces.length > 0 ||
    tokenUserFilters.appliedUsers.length > 0;

  // Fetch FILTERED token data when filters are applied
  const filteredTokenDataResult = useFilteredTokenUsageData(
    modelFilters.appliedModels,
    tokenWorkspaceFilters.appliedWorkspaces,
    tokenUserFilters.appliedUsers,
    {
      startTime: dateRangeState.startTime,
      endTime: dateRangeState.endTime,
      granularity: dateRangeState.granularity,
    },
    h2ogpteGroupBy
  );

  // Choose active token data: filtered or unfiltered
  const activeTokenDataResult = hasTokenFiltersApplied ? filteredTokenDataResult : tokenPrefetchResult;

  // Format token usage data for chart visualization
  const { formattedUsageData: formattedTokenUsageData, groups: tokenGroups } = useFormattedTokenUsageData(
    activeTokenDataResult.usageData,
    dateRangeState.granularity,
    h2ogpteGroupBy,
    enrichedWorkspaces.workspacesMap,
    enrichedWorkspaces.usersMap,
    tokenUsageType
  );

  // Calculate token usage statistics
  const tokenStats = useMemo<TokenUsageStats>(() => {
    return calculateTokenUsageStats(activeTokenDataResult.usageData, formattedTokenUsageData, tokenGroups);
  }, [activeTokenDataResult.usageData, formattedTokenUsageData, tokenGroups]);

  // Generate colors for token groups
  const tokenGroupColors = useGroupColors(tokenGroups);

  // ============================================
  // AI Units Filter State
  // ============================================

  // Determine if any filters are currently applied
  const hasFiltersApplied =
    serviceFilters.appliedServices.length > 0 ||
    workspaceFilters.appliedWorkspaces.length > 0 ||
    userFilters.appliedUsers.length > 0 ||
    tagFilters.appliedTags.length > 0;

  // State for immediate loading feedback when Apply button is clicked
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  // State for immediate loading feedback when Refresh button is clicked
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch FILTERED data when filters are applied (single query for current range only)
  const filteredDataResult = useFilteredUsageData(
    serviceFilters.appliedServices,
    workspaceFilters.appliedWorkspaces,
    userFilters.appliedUsers,
    tagFilters.appliedTags,
    {
      startTime: dateRangeState.startTime,
      endTime: dateRangeState.endTime,
      granularity: dateRangeState.granularity,
    },
    groupBy
  );

  // Choose which data source to use: filtered or unfiltered
  // - If filters are applied: use filtered data (from useFilteredUsageData)
  // - If no filters (or after reset): use unfiltered data (from useMultiDimensionalPrefetch)
  const activeDataResult = hasFiltersApplied ? filteredDataResult : usageDataResult;

  // Reset isApplyingFilters when filtered data finishes loading
  useEffect(() => {
    if (isApplyingFilters && !filteredDataResult.isLoading) {
      setIsApplyingFilters(false);
    }
  }, [isApplyingFilters, filteredDataResult.isLoading]);

  // Reset isRefreshing when data finishes loading after refresh
  useEffect(() => {
    if (isRefreshing) {
      const currentIsLoading = isH2oGPTeSelected ? tokenPrefetchResult.isLoading : usageDataResult.isLoading;
      if (!currentIsLoading) {
        setIsRefreshing(false);
      }
    }
  }, [isRefreshing, isH2oGPTeSelected, tokenPrefetchResult.isLoading, usageDataResult.isLoading]);

  // Format usage data for chart visualization (using active data source)
  const { formattedUsageData, groups } = useFormattedUsageData(
    activeDataResult.usageData,
    dateRangeState.granularity,
    groupBy,
    enrichedWorkspaces.workspacesMap,
    enrichedWorkspaces.usersMap
  );

  // Generate consistent colors for groups (shared between chart and table)
  const groupColors = useGroupColors(groups);

  // Calculate statistics
  const stats = calculateUsageStats(formattedUsageData, groups);

  // Handler for group by change
  const onChangeGroupBy = useCallback(
    (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (option) {
        setGroupBy(option.key as Grouping);
      }
    },
    [setGroupBy]
  );

  // Handler for resource type change
  const onChangeResource = useCallback(
    (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (option) {
        setResourceType(option.key as ResourceType);
      }
    },
    [setResourceType]
  );

  // Handler for H2OGPTe group by change
  const onChangeH2OGPTeGroupBy = useCallback(
    (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (option) {
        setH2OGPTeGroupBy(option.key as H2OGPTeGrouping);
      }
    },
    [setH2OGPTeGroupBy]
  );

  // Handler for token usage type change
  const onChangeTokenUsageType = useCallback((_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      setTokenUsageType(option.key as TokenUsageType);
    }
  }, []);

  // Empty stats to show when data is not loaded
  const emptyStats: UsageStats = { totalAiUnits: 0, maxAiUnits: 0, averageAiUnits: 0, uniqueCount: 0 };
  const emptyTokenStats: TokenUsageStats = {
    totalTokens: 0,
    inputTokens: 0,
    outputTokens: 0,
    maxTokens: 0,
    averageTokens: 0,
    uniqueCount: 0,
  };

  // Combined Apply handler - applies all filters together
  const handleApplyAllFilters = useCallback(() => {
    setIsApplyingFilters(true);
    if (isH2oGPTeSelected) {
      // H2OGPTe filters
      modelFilters.handleApply();
      tokenWorkspaceFilters.handleApply();
      tokenUserFilters.handleApply();
    } else {
      // AI Units filters
      serviceFilters.handleApply();
      workspaceFilters.handleApply();
      userFilters.handleApply();
      tagFilters.handleApply();
    }
  }, [
    isH2oGPTeSelected,
    serviceFilters,
    workspaceFilters,
    userFilters,
    tagFilters,
    modelFilters,
    tokenWorkspaceFilters,
    tokenUserFilters,
  ]);

  // Combined Reset handler - resets all filters together
  const handleResetAllFilters = useCallback(() => {
    if (isH2oGPTeSelected) {
      // H2OGPTe filters
      modelFilters.handleReset();
      tokenWorkspaceFilters.handleReset();
      tokenUserFilters.handleReset();
    } else {
      // AI Units filters
      serviceFilters.handleReset();
      workspaceFilters.handleReset();
      userFilters.handleReset();
      tagFilters.handleReset();
    }
  }, [
    isH2oGPTeSelected,
    serviceFilters,
    workspaceFilters,
    userFilters,
    tagFilters,
    modelFilters,
    tokenWorkspaceFilters,
    tokenUserFilters,
  ]);

  // Refresh chart handler - clears filters and refetches only current config
  const handleRefreshChart = useCallback(() => {
    // Set loading state immediately for visual feedback
    setIsRefreshing(true);
    // Clear all applied filters
    handleResetAllFilters();
    // Refetch only current date range + granularity + grouping (no filters)
    if (isH2oGPTeSelected) {
      tokenPrefetchResult.refetch();
    } else {
      usageDataResult.refetchCurrent?.();
    }
  }, [handleResetAllFilters, isH2oGPTeSelected, tokenPrefetchResult, usageDataResult]);

  // Combined filter state - check if any filter has changes or can be reset
  // Use token-specific filters when H2OGPTe is selected
  const hasAnyFilterChanges = isH2oGPTeSelected
    ? modelFilters.hasChanges || tokenWorkspaceFilters.hasChanges || tokenUserFilters.hasChanges
    : serviceFilters.hasChanges || workspaceFilters.hasChanges || userFilters.hasChanges || tagFilters.hasChanges;
  const hasAnyFiltersToReset = isH2oGPTeSelected
    ? modelFilters.hasFiltersToReset || tokenWorkspaceFilters.hasFiltersToReset || tokenUserFilters.hasFiltersToReset
    : serviceFilters.hasFiltersToReset ||
      workspaceFilters.hasFiltersToReset ||
      userFilters.hasFiltersToReset ||
      tagFilters.hasFiltersToReset;

  // Get current granularity and grouping labels for display
  const currentGranularityLabel =
    granularityOptions.find((option) => option.key === dateRangeState.granularity)?.label || 'Daily';
  const currentGroupByLabel = groupByOptions.find((option) => option.key === groupBy)?.text || 'Workspace';
  const currentH2OGPTeGroupByLabel =
    h2ogpteGroupByOptions.find((option) => option.key === h2ogpteGroupBy)?.text || 'Workspace';

  return (
    <Stack styles={stackStylesNoLeftNav}>
      <PageHeader pageTitle={pageTitle} description={subtitle} />

      <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 16 } }}>
        {/* Main content area */}
        <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
          {/* Stat Cards */}
          <MetricsCards
            stats={isH2oGPTeSelected ? emptyStats : stats}
            tokenStats={isH2oGPTeSelected ? tokenStats : emptyTokenStats}
            resourceType={resourceType}
            isLoading={
              isApplyingFilters ||
              isRefreshing ||
              (isH2oGPTeSelected ? tokenPrefetchResult.isLoading : activeDataResult.isLoading)
            }
            granularityLabel={currentGranularityLabel}
            groupByLabel={isH2oGPTeSelected ? currentH2OGPTeGroupByLabel : currentGroupByLabel}
          />

          {/* Chart */}
          <ChartContainer
            data={isH2oGPTeSelected ? formattedTokenUsageData : formattedUsageData}
            groups={isH2oGPTeSelected ? tokenGroups : groups}
            isLoading={
              isApplyingFilters ||
              isRefreshing ||
              (isH2oGPTeSelected ? tokenPrefetchResult.isLoading : activeDataResult.isLoading)
            }
            error={isH2oGPTeSelected ? tokenPrefetchResult.error : activeDataResult.error}
            onRetry={isH2oGPTeSelected ? tokenPrefetchResult.refetch : activeDataResult.refetch}
            onRefresh={handleRefreshChart}
            groupColors={isH2oGPTeSelected ? tokenGroupColors : groupColors}
            unitType={isH2oGPTeSelected ? 'tokens' : 'ai_units'}
          />

          {/* Data Table */}
          <TableContainer
            data={isH2oGPTeSelected ? formattedTokenUsageData : formattedUsageData}
            groups={isH2oGPTeSelected ? tokenGroups : groups}
            groupBy={isH2oGPTeSelected ? h2ogpteGroupBy : groupBy}
            isLoading={
              isApplyingFilters ||
              isRefreshing ||
              (isH2oGPTeSelected ? tokenPrefetchResult.isLoading : activeDataResult.isLoading)
            }
            startTime={dateRangeState.startTime}
            endTime={dateRangeState.endTime}
            groupColors={isH2oGPTeSelected ? tokenGroupColors : groupColors}
            unitType={isH2oGPTeSelected ? 'tokens' : 'ai_units'}
          />
        </Stack.Item>

        {/* Right side panel with controls */}
        <FilterPanel
          resourceType={resourceType}
          onChangeResource={onChangeResource}
          isResourceDisabled={false}
          startTime={dateRangeState.startTime}
          endTime={dateRangeState.endTime}
          granularity={dateRangeState.granularity}
          dateRangeType={dateRangeState.dateRangeType}
          onToggleDateRangeCallout={dateRangeState.onToggleDateRangeCallout}
          groupBy={groupBy}
          onChangeGroupBy={onChangeGroupBy}
          h2ogpteGroupBy={h2ogpteGroupBy}
          onChangeH2OGPTeGroupBy={onChangeH2OGPTeGroupBy}
          tokenUsageType={tokenUsageType}
          onChangeTokenUsageType={onChangeTokenUsageType}
          workspaceOptions={
            isH2oGPTeSelected ? tokenWorkspaceFilters.workspaceOptions : workspaceFilters.workspaceOptions
          }
          selectedWorkspaces={
            isH2oGPTeSelected ? tokenWorkspaceFilters.selectedWorkspaces : workspaceFilters.selectedWorkspaces
          }
          onChangeWorkspaceFilter={
            isH2oGPTeSelected ? tokenWorkspaceFilters.handleChange : workspaceFilters.handleChange
          }
          userOptions={isH2oGPTeSelected ? tokenUserFilters.userOptions : userFilters.userOptions}
          selectedUsers={isH2oGPTeSelected ? tokenUserFilters.selectedUsers : userFilters.selectedUsers}
          onChangeUserFilter={isH2oGPTeSelected ? tokenUserFilters.handleChange : userFilters.handleChange}
          serviceOptions={serviceFilters.serviceOptions}
          selectedServices={serviceFilters.selectedServices}
          onChangeServiceFilter={serviceFilters.handleChange}
          tagOptions={tagFilters.tagOptions}
          selectedTags={tagFilters.selectedTags}
          onChangeTagFilter={tagFilters.handleChange}
          modelOptions={modelFilters.modelOptions}
          selectedModels={modelFilters.selectedModels}
          onChangeModelFilter={modelFilters.handleChange}
          onApplyFilters={handleApplyAllFilters}
          onResetFilters={handleResetAllFilters}
          hasFilterChanges={hasAnyFilterChanges}
          hasFiltersToReset={hasAnyFiltersToReset}
        />
      </Stack>

      {/* Date Range Callout - rendered outside Stack to prevent layout shifts */}
      <DateRangeCallout
        isVisible={dateRangeState.calloutState.isVisible}
        targetElement={dateRangeState.calloutState.targetElement}
        startTime={dateRangeState.startTime}
        endTime={dateRangeState.endTime}
        granularity={dateRangeState.granularity}
        dateRangeType={dateRangeState.dateRangeType}
        onApply={dateRangeState.onApplyDateRange}
        onCancel={dateRangeState.onCancelDateRange}
      />
    </Stack>
  );
}

/**
 * PlatformUsage page wrapped with configuration provider.
 * Configuration is loaded from /platform-usage-config.yaml at runtime.
 */
function PlatformUsage() {
  return (
    <PlatformUsageConfigProvider>
      <PlatformUsageContent />
    </PlatformUsageConfigProvider>
  );
}

export default PlatformUsage;
